import { withRouter, useHistory } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import React, { useState, memo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import AIGetHelpIcon from "src/modules/3rd-party-management/assets/images/microchip/primary.png";
import Accordion from 'react-bootstrap/Accordion';
import {
    AnswerSheet,
    helpers
} from '@smartintegrity/questionnaire';
import {
    Col, Row, Button, Modal, ModalHeader, ModalBody,
    Spinner,
    Label,
    ModalFooter
} from "reactstrap";
import AIRiskAssessmentModal from "src/modules/3rd-party-management/components/RisksAndTasks/risks/ai/AssessmentModal";
import SupplierReportsService from "src/modules/3rd-party-management/apis/admin/SupplierReportsService";
import {
    CHAT_BOT_DOMAIN,
    API_BASE_URL
} from 'src/modules/3rd-party-management/constants';
import ReportDetailsPDF from './export/PDF';
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import { STANDARD_DATE_FORMAT } from "src/components/constants";
import DownloadAttachmentsButton from "./components/download-button";

const SupplierReportDetails = (props) => {
    const {
        organization,
        t, 
        reportId,
        supplierInfos,
        token
    } = props;
    const dateUtils = new DateUtils();

    const history = useHistory();

    const [questions, setQuestions] = useState([]);

    const [answers, setAnswers] = useState([]);

    const [ exportPDFInprocess, setExportPDFInprocess ] = useState(false);

    const [ aiBasedRiskAssessmentModalStatus, setAiBasedRiskAssessmentModalStatus ] = useState(false);

    const [ pdfObjectUrl, setPdfObjectUrl ] = useState(null);

    const handleFetchSupplierReportDetailsQuery = useQuery({
		queryKey: ['3rd-party-management-fetch-supplier-report-details', reportId],
		queryFn: async () => {
			const service = SupplierReportsService.getInstance();

            return await service.details(reportId, {});
		},
		cacheTime: 0,
		refetchOnWindowFocus: false,
		onError: (error) => {
			toast(t('An error occurred while fetching report details.'), {
				type: 'error',
			});
		},
	});

    const {
        refetch: fetchReportPDFData
    } = useQuery({
		queryKey: ['3rd-party-management-fetch-supplier-report-pdf-details', reportId],
		queryFn: async () => {
            setExportPDFInprocess(true);
			const service = SupplierReportsService.getInstance();

            return await service.fetchPDFData(reportId);
		},
		cacheTime: 0,
		refetchOnWindowFocus: false,
        enabled: false,
		onError: (error) => {
			toast(t('An error occurred while fetching report details.'), {
				type: 'error',
			});

            setExportPDFInprocess(false);
		},
        onSuccess: async ({
            answers,
            financialEndYear,
            responsiblePerson,
            submissionDate
        }) => {
            let fileName;
            if(supplierInfos.isOwnCompany){
                fileName = `company-${supplierInfos.id}_report-${reportId}.pdf`
            }
            else{
                fileName = `supplier-${supplierInfos.id}_report-${reportId}.pdf`
            }
            
            const blob = await pdf( 
                <ReportDetailsPDF 
                    organization={organization} 
                    supplierType={supplierInfos.isOwnCompany ? 'own' : 'creditor'}
                    data={{
                        sections:  groupQuestionsBySection(answers),
                        reportDetails: reportDetails,
                        companyName: supplierInfos.name,
                        assignedAnalyst: supplierInfos.assignedAnalyst,
                        financialEndYear: dateUtils.convertTimeStampToDate(financialEndYear, INTERNATIONAL_DATE_FORMAT),
                        submissionDate: dateUtils.convertTimeStampToDate(submissionDate, INTERNATIONAL_DATE_FORMAT),
                        responsiblePerson
                    }}
                /> 
            ).toBlob();
        
            saveAs(blob, fileName);

            setPdfObjectUrl(URL.createObjectURL(blob));
        }
	});

    useEffect(() => {
        if(handleFetchSupplierReportDetailsQuery?.data?.qa){
            setAnswers(
                helpers.makeAnswersReadyForAnswerSheet(
                    handleFetchSupplierReportDetailsQuery.data.qa, 
                    handleFetchSupplierReportDetailsQuery.data.qa
                )
            );

            setTimeout(() => {
                setQuestions(helpers.makeQuestionsReadyForAnswerSheet(
                    handleFetchSupplierReportDetailsQuery.data.qa,
                    true
                ))
            }, 1000);
        }
        
    }, [ handleFetchSupplierReportDetailsQuery.data ]);

    const findQuestionAnswers = (question) => {
        const answers = handleFetchSupplierReportDetailsQuery?.data?.qa || [];

        const questionIdToSearch = !question?.isReactSubQuestion ? question.id : question.parentQuestionId;

        const item = answers.find((questionData) => {
            return questionData.id === questionIdToSearch;
        });

        if(item){
            if(!question?.isReactSubQuestion){
                switch(question.type){
                    case 'textarea':
                    case 'text':
                    case 'date':
                    case 'select':
                    case 'country_list':
                        return item.answers[0]?.title;

                    case 'checkbox':
                    case 'category':
                    case 'file_upload':
                        return item.answers || [];

                    case 'radiobox':
                        const userAnswer = (item.answers && Array.isArray(item.answers) && item.answers.length > 0) ? item.answers[0] : null;

                        if(userAnswer){
                            const optionBaseInfos = question.options.find((option) => {
                                return option.id === userAnswer.value;
                            });

                            if(optionBaseInfos){
                                return {
                                    value       :   optionBaseInfos.id,
                                    title       :   optionBaseInfos.title,
                                    subTitle    :   (optionBaseInfos.type === 'date' || optionBaseInfos.type === 'text') ? userAnswer.title : null
                                };
                            }
                        }
                    break;
                    
                    default:
                        return null;
                }
            }
            else{
                const answer = item.answers[0];

                if(answer && answer?.children && Array.isArray(answer.children)){
                    let preFilledSubQuestionData = answer.children.find((subQuestionData) => {
                        return Number.parseInt(subQuestionData.id) === question.id;
                    });

                    if(preFilledSubQuestionData?.type === 'file_upload'){
                        return [{
                            title   :   preFilledSubQuestionData?.title,
                            name    :   preFilledSubQuestionData?.name
                        }];
                    }

                    return preFilledSubQuestionData?.title;
                }
            }
        }

        return null;
    }

    const groupQuestionsBySection = (questions) => {
        const groupedQuestionsBySection = [];

        const findSection = (sectionName) => {
            return groupedQuestionsBySection.find((section) => {
                return section.name === sectionName;
            });
        }

        for(let i = 0; i < questions.length; i++){
            const question = questions[i];
            let section = findSection(question.section || '');

            if(!section){
                section = {
                    name : question.section || '',
                    icon : question.icon,
                    items : []
                }
                groupedQuestionsBySection.push(section);
            }

            section.items.push(question);
        }

        return groupedQuestionsBySection;
    }

    const makeQuestionReadyForAI = (questions) => {
        let result = [];

        for(let i = 0; i < questions.length; i++){
            const question = questions[i];

            const questionResult = {
                question    :   question.title,
                answer      :   null
            };
            
            let answer = findQuestionAnswers(question);

            if(["checkbox", "category"].includes(question.type)){
                answer = (answer || []).map((ch) => {
                    return ch?.title;
                });
            }
            else if(question.type === 'radiobox'){
                const selectedOptionInfos = (question.options || []).find((o) => o.id === answer?.value);

                const children = ((selectedOptionInfos?.children && selectedOptionInfos.children.length > 0) ? selectedOptionInfos.children : []).map((child) => {
                    return {
                        ...child, 
                        isReactSubQuestion  :   true,
                        parentQuestionId    :   question.id
                    };
                });

                if(children.length > 0){
                    questionResult.children = makeQuestionReadyForAI(children);
                }

                answer = answer?.title;
            }
            else if(question.type === 'file_upload'){
                answer = (answer || []).length > 0 ? 'Yes' : 'No';
            }

            result.push({
                ...questionResult,
                answer  :   answer
            });
        }

        return result;
    }

    const renderLoadingSection = () => {
        return (
            <React.Fragment>
                <Row className="mb-4">
                    <Col sm="12">
                        <div className="loading-section p-5">
                            <Row className="mb-5">
                                <Col sm="12" md="3">
                                    <div className="dt-field dt-skeleton mb-1" style={{width: '40%'}}></div>
                                    <div className="dt-field dt-skeleton" style={{width: '70%'}}></div>
                                </Col>
                                <Col sm="12" md="3">
                                    <div className="dt-field dt-skeleton mb-1" style={{width: '40%'}}></div>
                                    <div className="dt-field dt-skeleton" style={{width: '70%'}}></div>
                                </Col>
                                <Col sm="12" md="3">
                                    <div className="dt-field dt-skeleton mb-1" style={{width: '40%'}}></div>
                                    <div className="dt-field dt-skeleton" style={{width: '70%'}}></div>
                                </Col>
                                <Col sm="12" md="3">
                                    <div className="dt-field dt-skeleton mb-1" style={{width: '40%'}}></div>
                                    <div className="dt-field dt-skeleton" style={{width: '70%'}}></div>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                </Row>
                <Row className="mb-4">
                    <Col lg="12">
                        <p style={{width: '90%'}} className={`dt-field dt-skeleton`}></p>
                    </Col>
                    <Col lg="6">
                        <p style={{width: '100%'}} className={`dt-field dt-skeleton`}></p>
                    </Col>
                    <Col lg="8">
                        <p style={{width: '100%'}} className={`dt-field dt-skeleton`}></p>
                    </Col>
                </Row>
                <Row className="mb-4">
                    <Col lg="12">
                        <p style={{width: '90%'}} className={`dt-field dt-skeleton`}></p>
                    </Col>
                    <Col lg="6">
                        <p style={{width: '100%'}} className={`dt-field dt-skeleton`}></p>
                    </Col>
                    <Col lg="8">
                        <p style={{width: '100%'}} className={`dt-field dt-skeleton`}></p>
                    </Col>
                </Row>
                <Row className="mb-4">
                    <Col lg="12">
                        <p style={{width: '90%'}} className={`dt-field dt-skeleton`}></p>
                    </Col>
                    <Col lg="6">
                        <p style={{width: '100%'}} className={`dt-field dt-skeleton`}></p>
                    </Col>
                    <Col lg="8">
                        <p style={{width: '100%'}} className={`dt-field dt-skeleton`}></p>
                    </Col>
                </Row>
            </React.Fragment>
        );
    }

    const renderAIRiskAssessmentModal = () => {
        return (
            aiBasedRiskAssessmentModalStatus && (
                <AIRiskAssessmentModal
                    closeModal={ () => setAiBasedRiskAssessmentModalStatus(false) }
                    onRisksCreated={ () => history.push({ search : '?tab=risk-assessment'}) }
                    isOpen={ aiBasedRiskAssessmentModalStatus }
                    translateResult={ true }
                    aiRequestOptions={{
                        requestUrl      :   `${CHAT_BOT_DOMAIN}api/tpdd/risks-associated-questioner.ai`,
                        payload    :   {
                            module              :   "TPDD_QUESTIONER",
                            // productGroup        :   (supplierInfos?.product || []).map((product) => {
                            //     return product?.name.productGroup;
                            // }),
                            productGroup        :   (supplierInfos?.industries || []).map((industry) => industry.id),
                            myLocation          :   organization?.countryId,
                            targetCountry       :   (supplierInfos?.locations || []).filter((location) => location.type === 'office')[0]?.country,
                            reportCaseId        :   reportId,
                            questions           :   makeQuestionReadyForAI(handleFetchSupplierReportDetailsQuery?.data?.qa || []),
                            organizationId      :   organization.id
                        }
                    }}
                    aiTranlateRequestOptions={{
                        requestUrl      :   `${CHAT_BOT_DOMAIN}api/tpdd/risks-associated-translate.ai`,
                        payload         :   {
                            module              :   "TPDD_QUESTIONER",
                            organizationId      :   organization.id,
                            reportCaseId        :   reportId
                        }
                    }}
                    additionnalDetailsRequestOptions={{
                        requestUrl  :   `${CHAT_BOT_DOMAIN}api/tpdd/risk-ai-questionnaire.get`,
                        payload     :   {}
                    }}
                    createRisksRequestOptions={{
                        requestUrl      :   `${API_BASE_URL}/risk_rate/create_ai`,
                        payload         :   {
                            supplier    :   supplierInfos.id,
                            reportCase  :   reportId
                        }
                    }}>
                    {(result) => {
                        return (
                            <Modal className="ai-risk-result-modal mw-100" style={{width: '90%'}} isOpen={aiBasedRiskAssessmentModalStatus} size="xl">
                                <ModalHeader toggle={() => setAiBasedRiskAssessmentModalStatus(!aiBasedRiskAssessmentModalStatus)}>
                                    {t('Risk Assesment Support by AI')}
                                </ModalHeader>
                                <ModalBody>
                                    <result.type {...result.props} />
                                </ModalBody>
                            </Modal>
                        )
                    }}
                </AIRiskAssessmentModal>
            )
        )
    }

    const renderExportPDFWaitingModal = () => {
        const openFileInNewTab = () => {
            window.open(pdfObjectUrl, '_blank');
        }

        const toggleWaitModal = () => {
            setExportPDFInprocess(false);
            setPdfObjectUrl(null);
        }

        return (
            <Modal 
                isOpen={ exportPDFInprocess } 
                toggle={toggleWaitModal}
            >
                <ModalHeader toggle={toggleWaitModal}>
                    <div>
                        {!pdfObjectUrl ? (
                            <Spinner
                                className="me-2"
                                animation="border"
                                variant="info"
                                size="sm"
                            />
                        ) : null}

                        {t(supplierInfos.isOwnCompany ? 'Company Report' : 'Third Party Report')}
                    </div>
                </ModalHeader>
                
                <ModalBody>
                    {pdfObjectUrl ? (
                        <>
                            <p>
                                {t('Your PDF has been successfully generated and downloaded.')}
                            </p>
                            <Button
                                color="secondary"
                                onClick={openFileInNewTab}
                                className="d-flex align-items-center"
                                size="sm"
                            >
                                <i className="ri-external-link-line font-size-20 me-1" />
                                {t('Open PDF in New Tab')}
                            </Button>
                        </>
                    ) : (
                        <>
                            {t('Exporting as PDF')}...
                        </>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button 
                        toggle={toggleWaitModal} 
                        color="secondary" 
                        outline
                    >
                        {t('Close')}
                    </Button>
                </ModalFooter>
            </Modal>
        )
    }

    if(handleFetchSupplierReportDetailsQuery.isFetching || handleFetchSupplierReportDetailsQuery.isLoading){
        return renderLoadingSection();
    }

    const { detail: reportDetails } = handleFetchSupplierReportDetailsQuery?.data || {}; 

    return (
        <React.Fragment>
            { renderAIRiskAssessmentModal() }

            { renderExportPDFWaitingModal() }

            <Row>
                <Col sm="12" className="d-flex flex-row-reverse justify-content-start align-items-center mb-3">
                    <Button color="primary" onClick={fetchReportPDFData}>
                        { t('Download Report PDF') }
                    </Button>

                    <DownloadAttachmentsButton 
                        reportId={ reportId }
                    />

                    {!supplierInfos.isOwnCompany && (
                        <Button color="primary" className="me-2 ai-base-risk-assessment-button"
                            onClick={ () => setAiBasedRiskAssessmentModalStatus(true) }
                            outline
                            >
                            <img alt="ai icon" src={AIGetHelpIcon} />
                            {t("Get help from AI")}
                        </Button>  
                    )}
                </Col>

                <Col sm="12" className="mb-3">
                    <Accordion className="custom-accordion" defaultActiveKey='report-details'>
                        <Accordion.Item eventKey='report-details'>
                            <Accordion.Body>
                                <Row>
                                    <Col sm="12" md="6" lg="4" className="mb-4">
                                        <div className="d-flex">
                                            <div className="avatar-sm me-3">
                                                <span className="avatar-title bg-light rounded-circle text-primary font-size-24">
                                                    <i className="ri-information-line"></i>
                                                </span>
                                            </div>
                                            <div className="flex-1 align-self-center overflow-hidden">
                                                <Label className="form-label">
                                                    {t('Report Type')}:
                                                </Label>
                                                <p className="text-muted mb-0">
                                                    {t(reportDetails.questionnaire?.title)}
                                                </p>
                                            </div>
                                        </div>
                                    </Col>

                                    <Col sm="12" md="6" lg="4" className="mb-4">
                                        <div className="d-flex">
                                            <div className="avatar-sm me-3">
                                                <span className="avatar-title bg-light rounded-circle text-primary font-size-24">
                                                    <i className="ri-shield-user-line"></i>
                                                </span>
                                            </div>
                                            <div className="flex-1 align-self-center overflow-hidden">
                                                <Label className="form-label">
                                                    {t(supplierInfos.isOwnCompany ? 'Responsible Person' : 'Contact Person')}:
                                                </Label>
                                                <p className="text-muted mb-0">
                                                    {reportDetails?.contactPerson?.name}
                                                </p>
                                            </div>
                                        </div>
                                    </Col> 

                                    <Col sm="12" md="6" lg="4" className="mb-4">
                                        <div className="d-flex">
                                            <div className="avatar-sm me-3">
                                                <span className="avatar-title bg-light rounded-circle text-primary font-size-24">
                                                    <i className="ri-money-dollar-circle-line"></i>
                                                </span>
                                            </div>
                                            <div className="flex-1 align-self-center overflow-hidden">
                                                <Label className="form-label">
                                                    {t('Financial Year')}:
                                                </Label>
                                                <p className="text-muted mb-0">
                                                    {reportDetails?.financialEndYear && (
                                                        <>
                                                            {dateUtils.convertTimeStampToDate(
                                                                reportDetails.financialEndYear,
                                                                'YYYY'
                                                            )}
                                                        </>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </Col>

                                    <Col sm="12" md="6" lg="4" className="mb-4">
                                        <div className="d-flex">
                                            <div className="avatar-sm me-3">
                                                <span className="avatar-title bg-light rounded-circle text-primary font-size-24">
                                                    <i className="ri-calendar-event-line"></i>
                                                </span>
                                            </div>
                                            <div className="flex-1 align-self-center overflow-hidden">
                                                <Label className="form-label">
                                                    {t('Submission Date')}:
                                                </Label>
                                                <p className="text-muted mb-0">
                                                    {reportDetails?.submissionDate && (
                                                        <>
                                                            {dateUtils.convertTimeStampToDate(
                                                                reportDetails.submissionDate,
                                                                INTERNATIONAL_DATE_FORMAT
                                                            )}
                                                        </>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </Col>

                                    <Col sm="12" md="6" lg="8" className="mb-4">
                                        <div className="d-flex">
                                            <div className="avatar-sm me-3">
                                                <span className="avatar-title bg-light rounded-circle text-primary font-size-24">
                                                    <i className="ri-calendar-todo-fill"></i>
                                                </span>
                                            </div>
                                            <div className="flex-1 align-self-center overflow-hidden">
                                                <Label className="form-label">
                                                    {t('Deadline')}:
                                                </Label>
                                                <p className="text-muted mb-0">
                                                    {reportDetails?.deadlineDate && (
                                                        <>
                                                            {dateUtils.convertTimeStampToDate(
                                                                reportDetails.deadlineDate,
                                                                STANDARD_DATE_FORMAT
                                                            )}
                                                        </>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </Col>

                <Col sm="12">
                    <AnswerSheet 
                        settings={{
                            buttons	:	{
                                back	:	{
                                    enabled	:	false
                                },
                                confirm	:	{
                                    enabled	:	false
                                }
                            },
                            fileDownloaderOptions: {
                                downloadRequestOptions: {
                                    headers: {
                                        Authorization: `Bearer ${token}`
                                    },
                                    payload: {},
                                    requestUrl: ({
                                        questionId,
                                        attachmentId
                                    }) => {
                                        return `${API_BASE_URL}/report/attachment/${attachmentId}/download`
                                    }
                                }
                            }
                        }}
                        questions={ questions }
                        answers={ answers }
                    />
                </Col>
            </Row>
        </React.Fragment>
    )
};

const mapStatetoProps = (state) => {
    const { Organization } = state;
    const { token } = state.Login;

    return {
        user            :   state.Login.user,
        organization    :   Organization,
        token
    };
};

export default withNamespaces()(
    withRouter(connect(
        mapStatetoProps,
        {}
    )(memo(SupplierReportDetails)))
);